// ============================================================
// New-message alerts, so they do not take over the screen (feedback
// 4 Nov 2026: six "Aadi Nair: Sent you a message" pop-ups stacked down
// the page, plus two Chrome pop-ups, and none said what the message was).
//
//  - In the app: at most MAX_ON_SCREEN pop-ups, the newest on top. A new
//    message from the same chat replaces that chat's pop-up instead of
//    adding one. Each shows who, where and the message itself.
//  - The Chrome / Windows pop-up only when the app is not in front (tab
//    hidden or window not focused), and once for all open tabs.
// ============================================================
import React from "react";
import { toast } from "react-toastify";

export const MAX_ON_SCREEN = 2;
const SHOW_MS = 6000;
const LONGEST = 280; // characters of the message shown

// The pop-ups on screen, oldest first: toast ids.
const onScreen = [];

/** The message text from a notification, whatever the server names it; "" when it carries none. */
export const messageText = (n) => {
  if (!n) return "";
  const raw = [n.message, n.Message, n.msg, n.text, n.content, n.body, n.last_message, n.Last_Message]
    .find((v) => typeof v === "string" && v.trim());
  if (!raw) return "";
  const text = raw
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > LONGEST ? `${text.slice(0, LONGEST - 1)}…` : text;
};

function ChatAlert({ who, what, where, text }) {
  return (
    <div style={{ lineHeight: 1.35, minWidth: 0 }}>
      <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{who}</div>
      <div style={{ fontSize: "0.8rem", opacity: 0.75 }}>
        {what}
        {where}
      </div>
      {text && (
        <div style={{ marginTop: 4, fontSize: "0.875rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{text}</div>
      )}
    </div>
  );
}

/**
 * Shows one in-app pop-up for a new message. `key` is the chat (one pop-up per chat); `onOpen` runs when it is
 * clicked.
 */
export const showChatToast = ({ key, who, what, where, text, onOpen }) => {
  const id = `chat-${key || "any"}`;
  const content = <ChatAlert who={who} what={what} where={where} text={text} />;
  const at = onScreen.indexOf(id);
  if (at >= 0 && toast.isActive(id)) {
    // Same chat: the pop-up shows the newest message and starts its time again.
    onScreen.splice(at, 1);
    onScreen.push(id);
    toast.update(id, { render: content, autoClose: SHOW_MS });
    return;
  }
  if (at >= 0) onScreen.splice(at, 1);
  // Room for this one: the oldest goes.
  while (onScreen.length >= MAX_ON_SCREEN) toast.dismiss(onScreen.shift());
  onScreen.push(id);
  toast.info(content, {
    toastId: id,
    position: "top-right",
    autoClose: SHOW_MS,
    closeOnClick: true,
    pauseOnHover: true,
    theme: "light",
    onClick: onOpen,
    onClose: () => {
      const i = onScreen.indexOf(id);
      if (i >= 0) onScreen.splice(i, 1);
    },
  });
};

/** Whether the person is looking at the app right now: then the in-app pop-up is enough. */
export const appInFront = () => {
  try {
    return document.visibilityState === "visible" && document.hasFocus();
  } catch (e) {
    return false;
  }
};

/**
 * Every open tab gets the same socket event. The first tab to claim a message shows its desktop pop-up; the
 * others skip it. `key` names the message (sender, chat and unread count).
 */
export const claimDesktopAlert = (key) => {
  try {
    const now = Date.now();
    const last = JSON.parse(window.localStorage.getItem("chatDesktopAlert") || "null");
    if (last && last.key === key && now - last.at < 5000) return false;
    window.localStorage.setItem("chatDesktopAlert", JSON.stringify({ key, at: now }));
    return true;
  } catch (e) {
    return true;
  }
};
